import {TemplateResultInfo} from "./types";

/**
 * Returns true if a template result should be considered a failure (has errors or success === false).
 * Centralized so failure-detection criteria stay consistent across the admin content UI.
 */
export function hasTemplateResultFailure(result: TemplateResultInfo): boolean {
  return (result.errors?.length ?? 0) > 0 || result.success === false; // skipcq: JS-0345 -- intentional: success is boolean|null, must distinguish false from null
}

/**
 * Parses an ISO date string as UTC when it has no timezone (backend sends naive UTC strings).
 * Use for comparing with Date.now() or other UTC timestamps so all consumers get correct results
 * regardless of user timezone.
 */
export function parseDateAsUtc(dateStr: string): number {
  const hasOffset = dateStr.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(dateStr);
  const normalized = hasOffset ? dateStr : `${dateStr}Z`;
  return new Date(normalized).getTime();
}

/**
 * Returns a short relative time label (e.g. "2m ago") for an ISO date string.
 * Uses parseDateAsUtc so backend naive-UTC datetimes are correct in all timezones.
 */
export function relativeTime(dateStr: string): string {
  const diff = Date.now() - parseDateAsUtc(dateStr);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Formats an ISO date string for display in the user's locale.
 * Uses parseDateAsUtc so backend naive-UTC datetimes render correctly in all timezones.
 */
export function formatDateForDisplay(dateStr: string): string {
  return new Date(parseDateAsUtc(dateStr)).toLocaleString();
}
